import apiClient from '../config/axiosConfig.js';

/**
 * Audio denoise (afftdn) API. Mirrors AudioSplitService.mjs — thin wrappers over
 * the shared apiClient (which injects auth + x-workspace-id); errors propagate
 * to the caller / the global axios interceptor.
 *
 * @typedef {Object} DenoiseParams
 * @property {number} nr  Noise reduction in dB (0.01–97).
 * @property {number} nf  Noise floor in dB (-80..-20).
 * @property {'w'|'v'|'s'} nt  Noise type: white / vinyl / shellac.
 * @property {boolean} tn  Adaptive noise tracking.
 */

/**
 * Generate a short denoised preview clip. Synchronous on the server — resolves
 * with a signed URL to play.
 * @param {string} sourceFileId
 * @param {DenoiseParams} params
 * @returns {Promise<{ success: boolean, url: string, expiresAt: number, params: DenoiseParams }>}
 */
export async function requestDenoisePreview(sourceFileId, params) {
    const res = await apiClient.post('/api/audio/denoise/preview', { sourceFileId, ...params });
    return res.data;
}

/**
 * Apply denoise to the whole file, saving the result as a new VERSION. Returns a
 * jobId to poll.
 * @param {string} sourceFileId
 * @param {DenoiseParams} params
 * @returns {Promise<{ success: boolean, jobId: string }>}
 */
export async function applyDenoise(sourceFileId, params) {
    const res = await apiClient.post('/api/audio/denoise/apply', { sourceFileId, ...params });
    return res.data;
}

/** Poll a denoise job's progress. Returns the DenoiseJob doc. */
export async function getDenoiseStatus(jobId) {
    const res = await apiClient.get('/api/audio/denoise/status', { params: { jobId } });
    return res.data.job;
}
